import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  FormGroup,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Loader from "Components/Common/Loader";

import CustomerContainer from "./customercontainer";
import { RootState, AppDispatch } from "store";
import { fetchLicProposalListThunk, deleteLicProposalAdminThunk } from "slices/KYC/thunk";
import { importExcelFileThunk } from "slices/lickyc/thunk";
import { distributorMapping, uploadBulkExcel } from "services/lickycService";
import { usePermissions } from "hooks/usePermissions";
import { PermissionEnum } from "Components/constants/permissions";
import NewDeleteModal from "Components/Common/NewDeleteModal";

const InsuranceCustomerPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const proposalList = useSelector((state: RootState) => (state as any).kyc?.proposalList);
  const loadingProposals = useSelector((state: RootState) => (state as any).kyc?.loadingProposals ?? false);
  const { loading: bulkImportLoading, errorMsg } = useSelector(
    (state: RootState) => (state as any).lickyc ?? {}
  );
  const loggedInUsername = useSelector(
    (state: RootState) => (state as any).Login?.username ?? ""
  );
  const permissions = usePermissions();
  const isSuperAdmin = permissions.includes(PermissionEnum.ALL);

  const bulkInputRef = useRef<HTMLInputElement>(null);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [consultantName, setConsultantName] = useState("");
  const [consultantCode, setConsultantCode] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteProposalId, setDeleteProposalId] = useState<number | null>(null);

  const proposals = proposalList?.proposals ?? [];
  const proposalPagination = proposalList?.pagination ?? {};
  const totalCustomers =
    proposalPagination.total ?? (proposals.length ? proposals.length : 0);
  const activeCustomers = proposalPagination.total_active ?? 0;
  const inactiveCustomers = proposalPagination.total_inactive ?? 0;
  const pendingCustomers = Math.max(
    0,
    totalCustomers - activeCustomers - inactiveCustomers
  );

  const refreshList = useCallback(() => {
    const query = searchQuery.trim();
    dispatch(
      fetchLicProposalListThunk({
        page,
        per_page: limit,
        from_date: "",
        to_date: "",
        status: "",
        proposal_number: query,
        chief_agent_name: query,
        chief_agent_number: query,
        proposal_id: 0,
        user_id: 0,
        customer_mobile: query,
      })
    );
  }, [dispatch, page, limit, searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      refreshList();
    }, 500);
    return () => clearTimeout(timer);
  }, [refreshList]);

  useEffect(() => {
    if (errorMsg?.message && errorMsg?.id) {
      toast.error(errorMsg.message);
    }
  }, [errorMsg?.message, errorMsg?.id]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const pageIds = proposals.map((p: any) => p.id).filter((id: any) => id != null);
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id: number) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const openMapModal = () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one proposal to map distributor.");
      return;
    }
    const distributorKey = loggedInUsername.trim();
    if (!isSuperAdmin && distributorKey) {
      setConsultantName(distributorKey);
      setConsultantCode(distributorKey);
    } else {
      setConsultantName("");
      setConsultantCode("");
    }
    setMapModalOpen(true);
  };

  const handleDistributorMapping = async () => {
    if (!consultantName.trim() || !consultantCode.trim()) {
      toast.error("Consultant name and code are required.");
      return;
    }
    setMappingLoading(true);
    try {
      const response = await distributorMapping({
        proposal_ids: selectedIds,
        consultant_name: consultantName.trim(),
        consultant_code: consultantCode.trim(),
      });
      if (response?.success === false) {
        toast.error(response?.message || "Unable to map distributor.");
      } else {
        toast.success(response?.message || "Distributor mapping has been done successfully");
        setMapModalOpen(false);
        setSelectedIds([]);
        refreshList();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Unable to map distributor.");
    } finally {
      setMappingLoading(false);
    }
  };

  const handleBulkFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBulk(true);
    try {
      // Upload via backend to avoid browser→Spaces CORS (Failed to fetch)
      const uploadRes = await uploadBulkExcel(file);
      const url =
        typeof uploadRes?.data === "string"
          ? uploadRes.data
          : (uploadRes as any)?.data?.url || (uploadRes as any)?.data;
      if (!url || typeof url !== "string") {
        toast.error(uploadRes?.message || "Upload failed.");
        return;
      }
      const result = await dispatch(importExcelFileThunk(url));
      if (result?.success) {
        toast.success(result.message ?? "File uploaded successfully.");
        refreshList();
      } else if (result && !result.success) {
        toast.error(result.message ?? "Upload failed.");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error?.message || "Upload failed."
      );
    } finally {
      setUploadingBulk(false);
      e.target.value = "";
    }
  };

  const handleDeleteClick = (id: number) => {
    setDeleteProposalId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteProposalId == null) return;
    const result = await dispatch(deleteLicProposalAdminThunk(deleteProposalId));
    setDeleteModalOpen(false);
    setDeleteProposalId(null);
    if (result?.status && String(result.status).toLowerCase() === "failed") {
      toast.error(result.message || "Unable to delete lic proposal.");
      return;
    }
    toast.success(result?.message || "Lic proposal has been deleted successfully");
    refreshList();
  };

  const totalFiltered = proposalPagination.total ?? proposals.length;
  const totalPages = Math.max(1, proposalPagination.total_pages ?? 1);

  useEffect(() => {
    document.title = "Insurance";
    return () => {
      document.title = "";
    };
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "select",
        header: () => (
          <Input
            type="checkbox"
            checked={allPageSelected}
            onChange={toggleSelectAll}
            aria-label="Select all"
          />
        ),
        accessorKey: "select",
        enableSorting: false,
        cell: (cell: any) => {
          const id = cell.row.original.id;
          if (id == null) return null;
          return (
            <Input
              type="checkbox"
              checked={selectedIds.includes(id)}
              onChange={() => toggleSelect(id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Select proposal ${id}`}
            />
          );
        },
      },
      {
        header: "ID",
        accessorKey: "id",
        cell: (cell: any) => `#LIC${cell.getValue() ?? "-"}`,
      },
      {
        header: "Consultant Name",
        accessorKey: "consultant_name",
        cell: (cell: any) => cell.getValue() ?? "-",
      },
      {
        header: "Consultant Code",
        accessorKey: "consultant_code",
        cell: (cell: any) => cell.getValue() ?? "-",
      },
      {
        header: "Chief Agent Name",
        accessorKey: "chief_agent_name",
        cell: (cell: any) => cell.getValue() ?? "-",
      },
      {
        header: "Chief Agent Number",
        accessorKey: "chief_agent_number",
        cell: (cell: any) => cell.getValue() ?? "-",
      },
      {
        header: "Proposal Deposit Amount",
        accessorKey: "proposal_deposit_amount",
        cell: (cell: any) => {
          const v = cell.getValue();
          return v != null ? String(v) : "-";
        },
      },
      {
        header: "Customer Mobile",
        accessorKey: "customer_mobile",
        cell: (cell: any) => cell.getValue() ?? "-",
      },
      {
        header: "Deposit Amt Type",
        accessorKey: "depositamttype",
        cell: (cell: any) => cell.getValue() ?? "-",
      },
      {
        header: "Inward Number",
        accessorKey: "inward_number",
        cell: (cell: any) => cell.getValue() ?? "-",
      },
      {
        header: "Date of Inward",
        accessorKey: "date_of_inward",
        cell: (cell: any) => {
          const v = cell.getValue();
          if (v == null || v === "") return "-";
          const d = new Date(v);
          return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
        },
      },
      {
        header: "Date of Proposal",
        accessorKey: "date_of_proposal",
        cell: (cell: any) => {
          const v = cell.getValue();
          if (v == null || v === "") return "-";
          const d = new Date(v);
          return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => {
          const value = cell.getValue();
          const displayVal =
            value != null && String(value).trim() !== ""
              ? String(value)
              : "Pending Customer Action";
          const lowerVal = displayVal.toLowerCase();
          const statusColor = lowerVal.includes("pending")
            ? "#A65F00"
            : lowerVal === "active"
            ? "#159E3A"
            : "#0D6EFD";
          return (
            <span
              className="d-inline-block text-center"
              style={{
                minWidth: "160px",
                border: `1px solid ${statusColor}`,
                borderRadius: "20px",
                color: statusColor,
                padding: "4px 12px",
                height: "auto",
                lineHeight: "18px",
                fontSize: "12px",
              }}
            >
              {displayVal}
            </span>
          );
        },
      },
      {
        header: "Signed Document",
        accessorKey: "signed_doc_url",
        cell: (cell: any) => {
          const url = cell.getValue();
          if (url == null || String(url).trim() === "") {
            return <span className="d-flex justify-content-center">-</span>;
          }
          const href = String(url).trim();
          return (
            <div className="d-flex justify-content-center">
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="text-danger text-decoration-none"
                title="Download signed document"
                aria-label="Download signed document"
                style={{ fontSize: "1.1rem" }}
              >
                <i className="ri-file-pdf-line" aria-hidden />
              </a>
            </div>
          );
        },
      },
      {
        header: "Actions",
        id: "actions",
        accessorKey: "id",
        cell: (cell: any) => {
          const id = cell.row.original.id;
          if (id == null) return null;
          return (
            <ul className="list-inline d-flex justify-content-center align-items-center mb-0">
              <li className="list-inline-item">
                <Button
                  color=""
                  className="btn btn-icon btn-soft-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/kyc/personal-details?proposal_id=${id}`);
                  }}
                  aria-label="Edit"
                  title="Edit"
                >
                  <i className="ri-pencil-fill" aria-hidden />
                </Button>
              </li>
              <li className="list-inline-item">
                <Button
                  color=""
                  className="btn btn-icon btn-soft-danger btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(id);
                  }}
                  aria-label="Delete"
                  title="Delete"
                >
                  <i className="ri-delete-bin-line" aria-hidden />
                </Button>
              </li>
            </ul>
          );
        },
      },
    ],
    [navigate, selectedIds, allPageSelected, handleDeleteClick]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <h4 className="mb-4 fw-bold" style={{ color: "#212529" }}>
          Insurance — Distributor Flow
        </h4>

        <Row className="mb-4 g-3">
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-3">
              <CardBody className="p-3">
                <h6 className="text-muted mb-1 small">Total Customers</h6>
                <h4 className="fw-bold mb-0">{String(totalCustomers).padStart(2, "0")}</h4>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-3">
              <CardBody className="p-3">
                <h6 className="text-muted mb-1 small">Active Customers</h6>
                <h4 className="fw-bold mb-0">{String(activeCustomers).padStart(2, "0")}</h4>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-3">
              <CardBody className="p-3">
                <h6 className="text-muted mb-1 small">Inactive Customers</h6>
                <h4 className="fw-bold mb-0">{String(inactiveCustomers).padStart(2, "0")}</h4>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-3">
              <CardBody className="p-3">
                <h6 className="text-muted mb-1 small">Pending Customers</h6>
                <h4 className="fw-bold mb-0">{String(pendingCustomers).padStart(2, "0")}</h4>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="align-items-center g-3 pb-4">
          <Col md={4}>
            <div className="position-relative">
              <Input
                type="text"
                value={searchQuery}
                placeholder="Search by ID, chief agent name, chief agent number or customer mobile…"
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="form-control border ps-5 rounded-3"
              />
              <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div>
          </Col>
          <Col md={2}>
            <Button color="light" className="rounded-3 border d-flex align-items-center gap-2" disabled>
              <i className="ri-filter-line" />
              Filter
            </Button>
          </Col>
          <Col md="auto" className="ms-auto d-flex gap-2">
            <Button
              color="secondary"
              className="px-4 rounded-3 d-flex align-items-center gap-2"
              disabled={selectedIds.length === 0 || mappingLoading}
              onClick={openMapModal}
            >
              <i className="ri-user-shared-line" />
              Map Distributor
            </Button>
            <input
              ref={bulkInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleBulkFileChange}
              style={{ display: "none" }}
            />
            <Button
              color="primary"
              className="px-4 rounded-3 d-flex align-items-center gap-2"
              disabled={uploadingBulk || bulkImportLoading}
              onClick={() => bulkInputRef.current?.click()}
            >
              {uploadingBulk || bulkImportLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  {uploadingBulk ? "Uploading…" : "Processing…"}
                </>
              ) : (
                <>
                  <i className="ri-upload-cloud-line" />
                  Bulk Upload
                </>
              )}
            </Button>
          </Col>
        </Row>

        <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
          {!loadingProposals ? (
            proposals.length > 0 || totalFiltered > 0 ? (
              <CustomerContainer
                columns={columns}
                data={proposals}
                pagination={{
                  page: proposalPagination.page ?? page,
                  total_items: totalFiltered,
                  total_pages: totalPages,
                  per_page: proposalPagination.per_page ?? limit,
                }}
                setLimit={setLimit}
                setPage={setPage}
                limit={limit}
                setRowId={() => {}}
                onPageChange={(newPage: number) => {
                  setPage(newPage);
                }}
              />
            ) : (
              <CardBody className="text-center py-5 text-muted">No records found.</CardBody>
            )
          ) : (
            <CardBody
              style={{ minHeight: "320px" }}
              className="d-flex align-items-center justify-content-center"
            >
              <Loader />
            </CardBody>
          )}
        </Card>
      </Container>

      <Modal isOpen={mapModalOpen} toggle={() => setMapModalOpen(false)} centered>
        <ModalHeader toggle={() => setMapModalOpen(false)}>Map Distributor</ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-3">
            Selected proposals: <strong>{selectedIds.length}</strong>
          </p>
          <FormGroup>
            <Label for="consultant_name">Consultant Name</Label>
            <Input
              id="consultant_name"
              value={consultantName}
              onChange={(e) => setConsultantName(e.target.value)}
              placeholder="Enter consultant name"
              readOnly={!isSuperAdmin}
            />
          </FormGroup>
          <FormGroup>
            <Label for="consultant_code">Consultant Code</Label>
            <Input
              id="consultant_code"
              value={consultantCode}
              onChange={(e) => setConsultantCode(e.target.value)}
              placeholder="Enter consultant code"
              readOnly={!isSuperAdmin}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setMapModalOpen(false)} disabled={mappingLoading}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleDistributorMapping} disabled={mappingLoading}>
            {mappingLoading ? "Saving…" : "Save Mapping"}
          </Button>
        </ModalFooter>
      </Modal>
      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteProposalId(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this customer proposal?"
      />
    </div>
  );
};

export default InsuranceCustomerPage;
